import { captureException } from "@sentry/browser";
import { isShowroomFakeId } from "lib/utils/showroom/showroomData";
import { useEffect, useState } from "react";
import { getLocationsWithHierarchy, type LocationWithHeirarchy } from "./locationUtils";

export const useLocationHierarchy = (builderId: string, locationId: string) => {
  const [data, setData] = useState<LocationWithHeirarchy | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  useEffect(() => {
    // Skip API call if IDs are missing or are fake showroom IDs (used in studio preview)
    if (!builderId || !locationId || isShowroomFakeId(builderId) || isShowroomFakeId(locationId)) {
      setData(undefined);
      setIsLoading(false);
      setError(undefined);
      return;
    }

    setData(undefined);
    setError(undefined);
    setIsLoading(true);

    let cancelled = false;

    async function fetchLocationHierarchy() {
      try {
        const hierarchyData = await getLocationsWithHierarchy(builderId, locationId);
        if (!cancelled) {
          setData(hierarchyData);
        }
      } catch (err) {
        captureException(err);
        console.error("Error fetching location hierarchy:", err);
        if (!cancelled) {
          setData(undefined);
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    void fetchLocationHierarchy();

    return () => {
      cancelled = true;
    };
  }, [builderId, locationId]);

  return { data, isLoading, error };
};
